/**
 * Category landing page — Communications hub.
 *
 * Shows large, descriptive sub-cards for each communications tool so a
 * first-time candidate can pick the right one without scanning a tiny
 * nav dropdown. Tools open on click; nothing is gated here (each
 * underlying route still enforces its own paywall via ProtectedRoute).
 */
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageSquareText, Mic, CalendarDays, Inbox, Image as ImageIcon } from "lucide-react";

const TOOLS = [
  {
    to: "/generator",
    title: "Social Post Generator",
    blurb: "Describe a moment — get Facebook, X, and Instagram drafts in your voice.",
    icon: MessageSquareText,
    tone: "red",
    testid: "comms-generator",
  },
  {
    to: "/speeches",
    title: "Speech Writer",
    blurb: "Full speeches or talking points for forums, dinners, and ribbon-cuttings.",
    icon: Mic,
    tone: "navy",
    testid: "comms-speeches",
  },
  {
    to: "/scheduler",
    title: "Social Media Scheduler",
    blurb: "Plan civic moments and custom posts across the cycle in one calendar.",
    icon: CalendarDays,
    tone: "navy",
    testid: "comms-scheduler",
  },
  {
    to: "/saved",
    title: "Saved Posts",
    blurb: "Every draft you've kept — search, edit, mark posted, and post again.",
    icon: Inbox,
    tone: "navy",
    testid: "comms-saved",
  },
  {
    to: "/graphics",
    title: "Graphics (Beta)",
    blurb: "Generate quick campaign graphics from your branding and a moment.",
    icon: ImageIcon,
    tone: "navy",
    testid: "comms-graphics",
  },
];

export default function Communications() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="page-communications">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy">
        <ArrowLeft className="h-4 w-4" /> Back to home
      </Link>
      <div className="mt-4">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red">Communications</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Get your message out.</h1>
        <p className="text-slate-600 mt-2 max-w-2xl text-base">Five tools for drafting, planning, and shipping campaign content — all grounded in your saved profile so the voice stays yours.</p>
      </div>
      <ToolGrid tools={TOOLS} />
    </div>
  );
}

export function ToolGrid({ tools }) {
  return (
    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-tool-grid">
      {tools.map((t) => {
        const Icon = t.icon;
        const iconCls = t.tone === "red" ? "bg-flag-red text-white" : "bg-flag-navy text-white";
        return (
          <Link
            key={t.to}
            to={t.to}
            data-testid={t.testid}
            className="group bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:border-flag-navy hover:shadow-md transition-all flex flex-col"
          >
            <div className={`h-12 w-12 rounded-xl ${iconCls} flex items-center justify-center`}>
              <Icon className="h-6 w-6" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-flag-navy">{t.title}</h2>
            <p className="mt-1.5 text-slate-600 text-[15px] leading-relaxed flex-1">{t.blurb}</p>
            <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-flag-red group-hover:gap-2 transition-all">
              Open <ArrowRight className="h-4 w-4" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}
